import { TFunction } from 'i18next';
import { Disaster, ErisStatusSearchingParameters } from '../../store/ErisInformationStore';
import { ElevatorStatus } from '../../models/StatusModel';

const getCurrentUserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export interface ErisCSVStatusExportRequestBody {
  latest: boolean;
  searchParams: Partial<ErisStatusSearchingParameters>;
  translation: {
    allGroups: string;
    all: string;
    recoveryStatuses: string[];
    confinementStatuses: string[];
    supportStatuses: string[];
    operationStatuses: string[];
    csvHeaders: string[];
  };
  dateTimeFormat: string;
  timeZone: string;
}

export const prepareCSVStatusRequestBody = (
  t: TFunction<'translation', undefined>,
  searchParameters: Partial<ErisStatusSearchingParameters>,
  latest: boolean = true,
  disaster?: Disaster | null
): ErisCSVStatusExportRequestBody => {
  return {
    latest: latest,
    searchParams: searchParameters,

    ...(disaster && {
      disasterId: disaster.id,
    }),
    translation: {
      allGroups: searchParameters.searchParams?.groupName
        ? searchParameters.searchParams?.groupName
        : t('eris.csvParamsStatus.allGroups'),
      all: searchParameters.searchParams?.operationStatus
        ? (() => {
            switch (searchParameters.searchParams.operationStatus) {
              case ElevatorStatus.communicationInProgress:
                return t('eris.csvParamsStatus.operationStatuses.communicationInProgress');
              case ElevatorStatus.recoveryComplete:
                return t('eris.csvParamsStatus.operationStatuses.recoveryComplete');
              case ElevatorStatus.noStatus:
                return t('eris.csvParamsStatus.operationStatuses.helios');
              case ElevatorStatus.suspended:
                return t('eris.csvParamsStatus.operationStatuses.suspended');
            }
          })()
        : t('eris.csvParamsStatus.operationStatuses.all'),
      recoveryStatuses: [
        t('eris.csvParamsStatus.recoveryStatuses.supported'),
        t('eris.csvParamsStatus.recoveryStatuses.notSupported'),
      ],
      confinementStatuses: [
        '',
        t('eris.common.entrapment.closure'),
        t('eris.common.entrapment.rescue'),
      ],
      supportStatuses: [
        t('eris.csvParamsStatus.supportStatuses.recovery'),
        t('eris.csvParamsStatus.supportStatuses.automaticRecovery'),
        t('eris.csvParamsStatus.supportStatuses.noAdmiddion'),
        t('eris.csvParamsStatus.supportStatuses.repairRequired'),
        t('eris.csvParamsStatus.supportStatuses.parking'),
      ],
      operationStatuses: [
        t('eris.csvParamsStatus.operationStatuses.recoveryComplete'),
        t('eris.csvParamsStatus.operationStatuses.suspended'),
        t('eris.csvParamsStatus.operationStatuses.communicationInProgress'),
        t('eris.csvParamsStatus.operationStatuses.helios'),
      ],
      csvHeaders: [
        t('eris.csvParamsStatus.csvHeaders.groupName'),
        t('eris.csvParamsStatus.csvHeaders.customerNumber'),
        t('eris.csvParamsStatus.csvHeaders.unit'),
        t('eris.csvParamsStatus.csvHeaders.buildingName'),
        t('eris.csvParamsStatus.csvHeaders.address'),
        t('eris.csvParamsStatus.csvHeaders.helios'),
        t('eris.csvParamsStatus.csvHeaders.sensor'),
        t('eris.csvParamsStatus.csvHeaders.receptionIndicator'),
        t('eris.csvParamsStatus.csvHeaders.confinment'),
        t('eris.csvParamsStatus.csvHeaders.availability'),
        t('eris.csvParamsStatus.csvHeaders.operationStatus'),
      ],
    },
    dateTimeFormat: 'yyyy/MM/dd HH:mm',
    timeZone: getCurrentUserTimeZone(),
  };
};

export interface ErisCSVSummaryExportRequestBody {
  latest: boolean;
  groupName: string;
  translation: {
    recoveryStatuses: string[];
    csvHeaders: string[];
    summaryRowLabels: string[];
  };
  dateTimeFormat: string;
  timeZone: string;
  userId: string;
}

export const prepareCSVSummaryRequestBody = (
  t: TFunction<'translation', undefined>,
  groupName: string,
  latest: boolean,
  userId: string,
  disaster?: Disaster | null
): ErisCSVSummaryExportRequestBody => {
  return {
    latest: latest,
    groupName: groupName,
    userId: userId,
    ...(disaster && {
      disasterId: disaster.id,
    }),
    translation: {
      recoveryStatuses: [
        t('eris.csvParamsSummary.recoveryStatuses.supported'),
        t('eris.csvParamsSummary.recoveryStatuses.notSupported'),
      ],
      csvHeaders: [
        t('eris.csvParamsSummary.csvHeaders.groupName'),
        t('eris.csvParamsSummary.csvHeaders.units'),
        t('eris.csvParamsSummary.csvHeaders.suspendedUnits'),
        t('eris.csvParamsSummary.csvHeaders.repairRequiredUnits'),
        t('eris.csvParamsSummary.csvHeaders.heliosConfirmedUnits'),
        t('eris.csvParamsSummary.csvHeaders.recoveredUnits'),
        t('eris.csvParamsSummary.csvHeaders.recoveryRate'),
        t('eris.csvParamsSummary.csvHeaders.confinedUnits'),
        t('eris.csvParamsSummary.csvHeaders.rescuedUnits'),
      ],
      summaryRowLabels: [t('eris.csvParamsSummary.csvHeaders.total')],
    },
    dateTimeFormat: 'yyyy/MM/dd HH:mm',
    timeZone: getCurrentUserTimeZone(),
  };
};
